import { FC, useEffect, useRef, useState } from 'react';
import LiteYouTubeEmbed from 'react-lite-youtube-embed';
import 'react-lite-youtube-embed/dist/LiteYouTubeEmbed.css';
import ReactPlayer from 'react-player';

import Icon from 'atoms/Icons/Icon';
import color from 'atoms/colors/colors';

// Quarks
import Container from 'quarks/Container';
import Flex from 'quarks/Flex';
import type { FlexProps } from 'quarks/interpolations/flex';

import { getYoutubeId } from 'utils/functions';

// Types
import type { ContentfulComponentVideo } from 'graphqlTypes';

interface VideoProps extends FlexProps, ContentfulComponentVideo {
  backgroundIsDark: boolean;
}

const ComponentVideo: FC<VideoProps> = ({ backgroundColor, internalName, thumbnail, video, videoUrl, ...props }) => {
  const [isPlay, setIsPlay] = useState(false);
  const [videoHeight, setVideoHeight] = useState(undefined);
  const isMp4Video = video?.file?.contentType === 'video/mp4';
  const youtubeID = getYoutubeId(videoUrl || '');
  const videoRef = useRef();
  const containerRef = useRef();

  useEffect(() => {
    if (!isMp4Video) {
      return;
    }

    const handleResize = () => {
      const videoEle = videoRef.current;
      const containerEle = containerRef.current;

      if (!videoEle || !containerEle) {
        return;
      }

      const width = containerEle.getBoundingClientRect().width;
      const aspectRatio = videoEle.videoWidth / videoEle.videoHeight;
      setVideoHeight(width / aspectRatio);
    };

    const setupEventListeners = () => {
      const videoEle = videoRef.current;

      if (!videoEle) {
        return;
      }

      if (videoEle.readyState >= 1) {
        handleResize();
      } else {
        videoEle.addEventListener('loadedmetadata', handleResize);
      }
    };

    const cleanupEventListeners = () => {
      window.removeEventListener('resize', handleResize);

      const videoEle = videoRef.current;
      if (videoEle) {
        videoEle.removeEventListener('loadedmetadata', handleResize);
      }
    };

    setupEventListeners();
    window.addEventListener('resize', handleResize);

    return cleanupEventListeners;
  }, [isMp4Video]);

  return (
    <>
      <Container
        ref={containerRef}
        position="relative"
        width="100%"
        maxWidth="100%"
        marginX="auto"
        borderRadius="6px"
        overflowX="hidden"
        overflowY="hidden"
        {...props}
        md={{ maxWidth: '770px', borderRadius: '12px' }}
        lg={{ maxWidth: '992px', borderRadius: '20px' }}
        onClick={() => setIsPlay(true)}
        css={`
          .w-big-play-button,
          .lty-playbtn {
            display: none !important;
          }
          video {
            object-fit: fill;
            width: 100%;
            display: flex;
            height: ${videoHeight}px;
          }
        `}
      >
        {isMp4Video ? (
          <video controls playsInline poster={thumbnail?.file?.url} ref={videoRef}>
            <source src={video?.file?.url} type="video/mp4"></source>
            <track kind="captions" />
          </video>
        ) : videoUrl?.includes('wistia') ? (
          <Container
            css={`
              position: relative;
              padding-top: 56.25%;
              .react-player {
                position: absolute;
                top: 0;
                left: 0;
              }
            `}
          >
            <ReactPlayer className="react-player" url={videoUrl} width="100%" height="100%" controls={true} />
          </Container>
        ) : (
          <LiteYouTubeEmbed
            id={youtubeID || ''}
            thumbnail={thumbnail?.file?.url || ''}
            poster="hqdefault"
            webp
            title={internalName || ''}
          />
        )}
        {!isPlay && (
          <Flex
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            width="64px"
            height="64px"
            borderRadius="50%"
            boxShadow="xxl"
            overflowX="hidden"
            overflowY="hidden"
            alignItems="center"
            justifyContent="center"
            cursor="pointer"
            display={isMp4Video ? 'none' : 'flex'}
            css={{
              background: 'rgba(255, 255, 255, 0.8)',
              border: '6px solid rgba(199, 199, 199, 0.5)',
              pointerEvents: 'none',
            }}
            sm={{ width: '80px', height: '80px', display: 'flex' }}
          >
            <Icon id="play-fill" fill={color.gray[900]} size={20} />
          </Flex>
        )}
      </Container>
      {backgroundColor !== 'None' && (
        <Flex
          width="100%"
          height="100%"
          maxHeight="70%"
          position="absolute"
          top="0"
          left="0"
          zIndex="-1"
          backgroundColor="navy-900"
          css={`
            clip-path: polygon(0% 50%, 0% 100%, 100% 60%, 100% 0%, 0% 0%);
          `}
        />
      )}
    </>
  );
};

export default ComponentVideo;
